import { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import { apiFetch, numAmt } from "./utils";
import { BRAND, KofidesLogo, Lbl, Inp, Slct, Btn, Card, ConfirmModal } from "./ui";
import { CedulasModule } from "./CedulasModule";

var TIPOS_CUENTA = [
  "Activo deudora","Activo acreedora",
  "Pasivo acreedora",
  "Capital deudora","Capital acreedora",
  "Resultados deudora","Resultados acreedora",
  "Orden deudora","Orden acreedora",
];
var MAYOR_OPTIONS = ["Sí","No","De título"];
var PAGE_SIZE = 50;
var EF_TABS = [
  {k:"catalogo", l:"Catálogo"},
  {k:"movimientos", l:"Movimientos"},
  {k:"cedulas", l:"Cédulas"},
];

function findCuenta(codigo, cuentas) {
  return cuentas.find(function(c){ return c.codigo===codigo; }) || null;
}

// nombreSubcuentaDe siempre se recalcula a partir de subcuentaDe (fuente de
// verdad) antes de guardar; nunca se pide ni se confia en el valor que venga
// del Excel, para que nunca quede desincronizado del nombre real del padre.
function resolveNombreSubcuentaDe(subcuentaDe, cuentas) {
  if (!subcuentaDe) return null;
  var parent = findCuenta(subcuentaDe, cuentas);
  return parent ? parent.nombre : null;
}

function recomputeNombres(list) {
  return list.map(function(c){
    return Object.assign({}, c, {nombreSubcuentaDe: resolveNombreSubcuentaDe(c.subcuentaDe, list)});
  });
}

function wouldCreateCycle(codigo, subcuentaDe, cuentas) {
  var current = subcuentaDe;
  var seen = {};
  while (current) {
    if (current===codigo) return true;
    if (seen[current]) return false;
    seen[current] = true;
    var parent = findCuenta(current, cuentas);
    current = parent ? parent.subcuentaDe : null;
  }
  return false;
}

// ── Fase 2: Movimientos Auxiliares del Catalogo ─────────────────────────────

var RE_CODIGO_CUENTA = /^\d{3}-\d{3}-\d{3}$/;
var RE_FECHA_DDMMYYYY = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
var RE_TOTAL_HOJA = /^Total .+:$/;

function cell(row, i) {
  var v = row[i];
  return String(v!==undefined && v!==null ? v : "").trim();
}

function fechaDDMMYYYYaISO(s) {
  var partes = String(s).trim().split("/");
  if (partes.length!==3) return null;
  var yyyy = partes[2];
  if (yyyy.length!==4) return null;
  var mm = partes[1].padStart(2,"0");
  var dd = partes[0].padStart(2,"0");
  return yyyy+"-"+mm+"-"+dd;
}

function round2(n) { return Math.round((Number(n)||0)*100)/100; }

// Recorre las filas del Excel (ya sin las 6 de encabezado) manteniendo la
// cuenta actual: cada fila de cuenta actualiza cuentaActual (codigo sin
// guiones); cada fila de fecha es un movimiento de esa cuenta.
function parseMovimientosRows(rows) {
  var cuentaActual = null;
  var nombresPorCodigo = {};
  var movimientos = [];
  var totalArchivo = null;

  rows.slice(6).forEach(function(row) {
    row = row || [];
    var c0 = cell(row,0);

    if (RE_CODIGO_CUENTA.test(c0)) {
      cuentaActual = c0.replace(/-/g,"");
      nombresPorCodigo[cuentaActual] = cell(row,2);
      return;
    }
    if (cell(row,4)==="Total contable:") {
      totalArchivo = {cargos: numAmt(row[6]), abonos: numAmt(row[7])};
      return;
    }
    if (c0==="" && RE_TOTAL_HOJA.test(cell(row,1))) {
      return; // subtotal de hoja
    }
    if (RE_FECHA_DDMMYYYY.test(c0) && cuentaActual) {
      var iso = fechaDDMMYYYYaISO(c0);
      if (!iso) return;
      movimientos.push({
        codigoCuenta: cuentaActual,
        fecha: iso,
        tipo: cell(row,2),
        numero: cell(row,3),
        concepto: cell(row,4),
        referencia: cell(row,5),
        cargos: numAmt(row[6]),
        abonos: numAmt(row[7]),
        saldo: numAmt(row[8]),
      });
    }
    // fila en blanco u otro contenido no reconocido: se ignora
  });

  return {movimientos: movimientos, nombresPorCodigo: nombresPorCodigo, totalArchivo: totalArchivo};
}

// Valida antes de tocar el backend: si algo falla, no se llama al servidor.
// El servidor repite estas mismas validaciones de forma autoritativa.
function validarMovimientosParseados(parsed, cuentas) {
  if (!parsed.movimientos.length) {
    return "No se encontraron movimientos validos en el archivo. Verifica que las fechas esten en formato DD/MM/AAAA y que el encabezado ocupe las primeras 6 filas.";
  }
  if (!parsed.totalArchivo) {
    return "No se encontro la fila \"Total contable:\" en el archivo. No se puede validar el cuadre de la importacion.";
  }
  var sumaCargos = round2(parsed.movimientos.reduce(function(s,m){return s+m.cargos;},0));
  var sumaAbonos = round2(parsed.movimientos.reduce(function(s,m){return s+m.abonos;},0));
  var totCargos = round2(parsed.totalArchivo.cargos);
  var totAbonos = round2(parsed.totalArchivo.abonos);
  if (Math.abs(sumaCargos-totCargos)>0.01 || Math.abs(sumaAbonos-totAbonos)>0.01) {
    return "La suma de los movimientos no cuadra con \"Total contable:\" del archivo. Cargos: suma "+sumaCargos.toFixed(2)+" vs total "+totCargos.toFixed(2)+". Abonos: suma "+sumaAbonos.toFixed(2)+" vs total "+totAbonos.toFixed(2)+". No se importo nada.";
  }

  var codigosCatalogo = {};
  cuentas.forEach(function(c){ codigosCatalogo[c.codigo]=true; });
  var codigosArchivo = {};
  parsed.movimientos.forEach(function(m){ codigosArchivo[m.codigoCuenta]=true; });
  var faltantes = Object.keys(codigosArchivo).filter(function(cod){ return !codigosCatalogo[cod]; });
  if (faltantes.length>0) {
    var detalle = faltantes.map(function(cod){ return cod+" ("+(parsed.nombresPorCodigo[cod]||"sin nombre")+")"; }).join(", ");
    return "No se importo nada: "+faltantes.length+" cuenta(s) del archivo no existen en el catalogo: "+detalle+".";
  }
  return null;
}

function calcularRango(movimientos) {
  var fechas = movimientos.map(function(m){return m.fecha;}).sort();
  return {desde: fechas[0], hasta: fechas[fechas.length-1]};
}

function splitRubroNaturaleza(tipo) {
  var t = String(tipo||"").trim();
  var idx = t.lastIndexOf(" ");
  if (idx===-1) return {rubro:t, naturaleza:""};
  return {rubro: t.slice(0,idx), naturaleza: t.slice(idx+1)};
}

function formatCuentaConGuiones(codigo) {
  if (!codigo || codigo.length!==9) return codigo||"";
  return codigo.slice(0,3)+"-"+codigo.slice(3,6)+"-"+codigo.slice(6,9);
}

var COLUMNAS_MOV = ["Nivel 1","Nivel 2","Nivel 3","Cuenta","Nombre","Fecha","Año","Mes","Tipo","Número","Concepto","Referencia","Cargos","Abonos","Saldo","Naturaleza","Rubro"];

var thFilterStyle = {padding:"6px 8px",verticalAlign:"top"};
var filterCtrlSx = {padding:"4px 6px",fontSize:11,minWidth:64,background:"#fff"};

function ImportMovConfirmModal(p) {
  if (!p.data) return null;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:14,padding:24,width:"100%",maxWidth:420,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <p style={{fontSize:15,fontWeight:600,color:"#0f172a",margin:"0 0 20px",textAlign:"center"}}>
          Este archivo contiene movimientos del {p.data.rango.desde} al {p.data.rango.hasta}. Esto eliminara los movimientos ya guardados en ese rango y los sustituira por los de este archivo. ¿Confirmas?
        </p>
        <div style={{display:"flex",gap:10}}>
          <button onClick={p.onCancel} disabled={p.saving} style={{flex:1,padding:"10px 0",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancelar</button>
          <Btn onClick={p.onConfirm} disabled={p.saving} sx={{flex:1,opacity:p.saving?0.7:1}}>{p.saving?"Guardando...":"Confirmar"}</Btn>
        </div>
      </div>
    </div>
  );
}

function CuentaFormModal(p) {
  var e = p.editing || {};
  var isEditing = !!p.editing;
  var [codigo, setCodigo] = useState(e.codigo||"");
  var [nombre, setNombre] = useState(e.nombre||"");
  var [subcuentaDe, setSubcuentaDe] = useState(e.subcuentaDe||"");
  var [tipo, setTipo] = useState(e.tipo||TIPOS_CUENTA[0]);
  var [activa, setActiva] = useState(e.activa!==undefined?e.activa:true);
  var [moneda, setMoneda] = useState(e.moneda||"MXN");
  var [cuentaDeMayor, setCuentaDeMayor] = useState(e.cuentaDeMayor||"No");
  var [localError, setLocalError] = useState("");

  var codTrim = codigo.trim();
  var subTrim = subcuentaDe.trim();
  var parentAccount = subTrim ? findCuenta(subTrim, p.cuentas) : null;
  var parentMissing = subTrim.length>0 && !parentAccount;
  var selfRef = subTrim.length>0 && subTrim===codTrim;
  var cycle = subTrim.length>0 && !selfRef && wouldCreateCycle(codTrim, subTrim, p.cuentas);

  function validate() {
    if (!codTrim) return "El codigo es obligatorio.";
    if (!nombre.trim()) return "El nombre es obligatorio.";
    if (!isEditing && findCuenta(codTrim, p.cuentas)) return "Ya existe una cuenta con ese codigo.";
    if (selfRef) return "Una cuenta no puede ser subcuenta de si misma.";
    if (parentMissing) return "No existe ninguna cuenta con el codigo \""+subTrim+"\" en el catalogo. No se permiten cuentas huerfanas: crea primero la cuenta padre.";
    if (cycle) return "Esa jerarquia crearia un ciclo: la cuenta padre elegida depende, directa o indirectamente, de esta misma cuenta.";
    return "";
  }

  function submit() {
    var msg = validate();
    if (msg) { setLocalError(msg); return; }
    setLocalError("");
    p.onSave({
      codigo: codTrim,
      nombre: nombre.trim(),
      subcuentaDe: subTrim || null,
      tipo: tipo,
      activa: !!activa,
      moneda: moneda.trim() || "MXN",
      cuentaDeMayor: cuentaDeMayor,
      origen: e.origen || "manual",
    });
  }

  var shownError = localError || p.error || "";

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:14,padding:28,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <h2 style={{fontSize:16,fontWeight:700,margin:0}}>{isEditing?"Editar cuenta":"Agregar cuenta"}</h2>
          <button onClick={p.onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#94a3b8"}}>X</button>
        </div>
        <div style={{display:"grid",gap:14}}>
          <div>
            <Lbl c="Codigo"/>
            <Inp placeholder="101000000" value={codigo} disabled={isEditing} onChange={function(ev){setCodigo(ev.target.value);}}/>
          </div>
          <div><Lbl c="Nombre"/><Inp placeholder="Caja" value={nombre} onChange={function(ev){setNombre(ev.target.value);}}/></div>
          <div>
            <Lbl c="Subcuenta de (codigo de la cuenta padre, opcional)"/>
            <Inp placeholder="Dejar vacio si es cuenta raiz" value={subcuentaDe} onChange={function(ev){setSubcuentaDe(ev.target.value);}}/>
            {subTrim?(
              parentAccount?(
                <p style={{margin:"5px 0 0",fontSize:12,color:"#16a34a"}}>Nombre de subcuenta de: {parentAccount.nombre}</p>
              ):(
                <p style={{margin:"5px 0 0",fontSize:12,color:"#dc2626"}}>No existe ninguna cuenta con este codigo todavia.</p>
              )
            ):null}
          </div>
          <div>
            <Lbl c="Tipo"/>
            <Slct value={tipo} onChange={function(ev){setTipo(ev.target.value);}}>
              {TIPOS_CUENTA.map(function(t){return <option key={t} value={t}>{t}</option>;})}
            </Slct>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div><Lbl c="Moneda"/><Inp value={moneda} onChange={function(ev){setMoneda(ev.target.value.toUpperCase());}}/></div>
            <div>
              <Lbl c="Cuenta de mayor"/>
              <Slct value={cuentaDeMayor} onChange={function(ev){setCuentaDeMayor(ev.target.value);}}>
                {MAYOR_OPTIONS.map(function(o){return <option key={o} value={o}>{o}</option>;})}
              </Slct>
            </div>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#374151",cursor:"pointer"}}>
            <input type="checkbox" checked={activa} onChange={function(ev){setActiva(ev.target.checked);}}/>
            Cuenta activa
          </label>
        </div>
        {shownError?<div style={{marginTop:16,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#dc2626"}}>{shownError}</div>:null}
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button onClick={p.onClose} disabled={p.saving} style={{flex:1,padding:"10px 0",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancelar</button>
          <Btn onClick={submit} disabled={p.saving} sx={{flex:2,padding:"10px 0",borderRadius:9,opacity:p.saving?0.7:1}}>{p.saving?"Guardando...":"Guardar"}</Btn>
        </div>
      </div>
    </div>
  );
}

export function EstadosFinancierosModule(p) {
  var uid = p.user.id;
  var role = p.role || p.user.role;
  var isElevated = role==="contador"||role==="superadmin";
  var showClientSelector = isElevated && Array.isArray(p.clients) && p.clients.length>0 && typeof p.onChangeClient==="function";

  var [cuentas, setCuentas] = useState([]);
  var [loaded, setLoaded] = useState(false);
  var [search, setSearch] = useState("");
  var [page, setPage] = useState(0);
  var [formState, setFormState] = useState(null); // null=cerrado, {}=nueva cuenta, cuenta=editar
  var [saving, setSaving] = useState(false);
  var [formError, setFormError] = useState("");
  var [actionError, setActionError] = useState(null);
  var [confirmDelete, setConfirmDelete] = useState(null);
  var [deleting, setDeleting] = useState(false);
  var [importing, setImporting] = useState(false);
  var [importError, setImportError] = useState(null);
  var [importSummary, setImportSummary] = useState(null);
  var fileInputRef = useRef();

  var [activeTab, setActiveTab] = useState("catalogo");
  var [movimientos, setMovimientos] = useState([]);
  var [loadedMov, setLoadedMov] = useState(false);
  var [pageMov, setPageMov] = useState(0);
  var [importingMov, setImportingMov] = useState(false);
  var [savingMov, setSavingMov] = useState(false);
  var [importErrorMov, setImportErrorMov] = useState(null);
  var [importSummaryMov, setImportSummaryMov] = useState(null);
  var [pendingImport, setPendingImport] = useState(null);
  var fileInputMovRef = useRef();
  var [filtroNivel1, setFiltroNivel1] = useState("");
  var [filtroNivel2, setFiltroNivel2] = useState("");
  var [filtroNivel3, setFiltroNivel3] = useState("");
  var [filtroCuenta, setFiltroCuenta] = useState("");
  var [filtroNombre, setFiltroNombre] = useState("");
  var [filtroDesde, setFiltroDesde] = useState("");
  var [filtroHasta, setFiltroHasta] = useState("");
  var [filtroAnio, setFiltroAnio] = useState("");
  var [filtroMes, setFiltroMes] = useState("");
  var [filtroTipo, setFiltroTipo] = useState("");
  var [filtroNumero, setFiltroNumero] = useState("");
  var [filtroConcepto, setFiltroConcepto] = useState("");
  var [filtroReferencia, setFiltroReferencia] = useState("");
  var [filtroCargosMin, setFiltroCargosMin] = useState("");
  var [filtroCargosMax, setFiltroCargosMax] = useState("");
  var [filtroAbonosMin, setFiltroAbonosMin] = useState("");
  var [filtroAbonosMax, setFiltroAbonosMax] = useState("");
  var [filtroSaldoMin, setFiltroSaldoMin] = useState("");
  var [filtroSaldoMax, setFiltroSaldoMax] = useState("");
  var [filtroNaturaleza, setFiltroNaturaleza] = useState("");
  var [filtroRubro, setFiltroRubro] = useState("");
  var [filtroGeneral, setFiltroGeneral] = useState("");

  useEffect(function(){
    setLoaded(false);
    apiFetch("/catalogs/"+uid+"/cuentas_contables").then(function(r){return r.json();}).then(function(d){
      setCuentas(Array.isArray(d.data)?d.data:[]);
      setLoaded(true);
    }).catch(function(){setLoaded(true);});
  },[uid]);

  useEffect(function(){
    setLoadedMov(false);
    apiFetch("/catalogs/"+uid+"/movimientos_contables").then(function(r){return r.json();}).then(function(d){
      setMovimientos(Array.isArray(d.data)?d.data:[]);
      setLoadedMov(true);
    }).catch(function(){setLoadedMov(true);});
  },[uid]);

  useEffect(function(){ setPage(0); },[search]);
  useEffect(function(){ setPageMov(0); },[
    filtroNivel1, filtroNivel2, filtroNivel3, filtroCuenta, filtroNombre,
    filtroDesde, filtroHasta, filtroAnio, filtroMes, filtroTipo,
    filtroNumero, filtroConcepto, filtroReferencia,
    filtroCargosMin, filtroCargosMax, filtroAbonosMin, filtroAbonosMax, filtroSaldoMin, filtroSaldoMax,
    filtroNaturaleza, filtroRubro, filtroGeneral,
  ]);

  function saveCuentasCatalog(data) {
    return apiFetch("/catalogs/"+uid+"/cuentas_contables",{method:"POST",body:JSON.stringify({data:data})}).then(function(r){
      if (!r.ok) {
        return r.json().catch(function(){return {};}).then(function(d){
          throw new Error(d.error || ("El servidor rechazo el guardado (HTTP "+r.status+")."));
        });
      }
      return r;
    });
  }

  async function handleUpdateRfcManual(codigo, rfcManual) {
    var next = cuentas.map(function(c){ return c.codigo===codigo ? Object.assign({},c,{rfcManual: rfcManual||null}) : c; });
    await saveCuentasCatalog(next);
    setCuentas(next);
  }

  async function handleSaveAccount(data) {
    setSaving(true);
    setFormError("");
    var isEditing = formState && formState.codigo;
    var next = isEditing
      ? cuentas.map(function(c){ return c.codigo===data.codigo ? Object.assign({},c,data) : c; })
      : cuentas.concat([data]);
    next = recomputeNombres(next);
    try {
      await saveCuentasCatalog(next);
      setCuentas(next);
      setFormState(null);
    } catch (err) {
      console.error("[EF-CUENTA] No se pudo guardar la cuenta:", err);
      setFormError("No se pudo guardar la cuenta en el servidor (Motivo: "+(err.message||"error desconocido")+"). Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    try {
      var r = await apiFetch("/catalogs/"+uid+"/cuentas_contables/"+encodeURIComponent(confirmDelete.codigo), {method:"DELETE"});
      if (!r.ok) {
        var d = await r.json().catch(function(){return {};});
        throw new Error(d.error || ("El servidor rechazo el borrado (HTTP "+r.status+")."));
      }
      var next = recomputeNombres(cuentas.filter(function(c){return c.codigo!==confirmDelete.codigo;}));
      setCuentas(next);
      setConfirmDelete(null);
    } catch (err) {
      console.error("[EF-CUENTA] No se pudo eliminar la cuenta:", err);
      setActionError("No se pudo eliminar la cuenta "+confirmDelete.codigo+". Motivo: "+(err.message||"error desconocido"));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  function handleImportFile(file) {
    setImporting(true); setImportError(null); setImportSummary(null);
    var reader = new FileReader();
    reader.onload = async function(ev) {
      try {
        var wb = XLSX.read(ev.target.result, {type:"array"});
        var ws = wb.Sheets[wb.SheetNames[0]];
        // raw:false para que SheetJS devuelva el texto formateado de cada
        // celda (no el numero crudo), y no perder ceros a la izquierda en
        // codigos de cuenta que Excel interprete como numericos.
        var rows = XLSX.utils.sheet_to_json(ws, {defval:"", raw:false});
        var byCodigo = {};
        cuentas.forEach(function(c){ byCodigo[c.codigo] = c; });
        var added = 0, updated = 0;
        rows.forEach(function(row){
          var codigo = String(row["Cuenta"]!==undefined&&row["Cuenta"]!==null?row["Cuenta"]:"").trim();
          if (!codigo) return;
          var subRaw = row["Subcuenta de"];
          var subcuentaDe = (subRaw===undefined||subRaw===null||String(subRaw).trim()==="") ? null : String(subRaw).trim();
          var activaTxt = String(row["Activa"]||"").trim().toLowerCase();
          var cuenta = {
            codigo: codigo,
            nombre: String(row["Nombre"]||"").trim(),
            subcuentaDe: subcuentaDe,
            tipo: String(row["Tipo"]||"").trim(),
            activa: activaTxt==="activa",
            moneda: String(row["Moneda"]||"MXN").trim()||"MXN",
            cuentaDeMayor: String(row["Cuenta de mayor"]||"No").trim()||"No",
            origen: "importado",
          };
          if (byCodigo[codigo]) updated++; else added++;
          byCodigo[codigo] = cuenta;
        });

        if (added===0 && updated===0) {
          setImportError("No se encontraron filas validas en el archivo. Verifica que la columna \"Cuenta\" tenga datos y que el encabezado este en la primera fila.");
          return;
        }

        var merged = Object.keys(byCodigo).map(function(k){return byCodigo[k];});

        var huerfanas = merged.filter(function(c){ return c.subcuentaDe && !byCodigo[c.subcuentaDe]; });
        if (huerfanas.length>0) {
          var ejemplos = huerfanas.slice(0,5).map(function(c){return c.codigo+" (subcuenta de "+c.subcuentaDe+")";}).join(", ");
          setImportError("El archivo tiene "+huerfanas.length+" cuenta(s) huerfana(s): su codigo padre en \"Subcuenta de\" no existe en el catalogo ni en el propio archivo. No se importo nada. Ejemplos: "+ejemplos+".");
          return;
        }

        var ciclos = merged.filter(function(c){ return c.subcuentaDe && wouldCreateCycle(c.codigo, c.subcuentaDe, merged); });
        if (ciclos.length>0) {
          var ejemplosCiclo = ciclos.slice(0,5).map(function(c){return c.codigo;}).join(", ");
          setImportError("El archivo tiene "+ciclos.length+" cuenta(s) cuya \"Subcuenta de\" crea un ciclo (la cuenta depende, directa o indirectamente, de si misma): "+ejemplosCiclo+". Corrige el archivo y vuelve a importar. No se importo nada.");
          return;
        }

        merged = recomputeNombres(merged);
        await saveCuentasCatalog(merged);
        setCuentas(merged);
        setImportSummary({added:added, updated:updated, total:merged.length});
      } catch (ex) {
        console.error("[EF-IMPORT] Fallo la importacion:", ex);
        setImportError(ex&&ex.message ? ("No se pudo completar la importacion. No se importo nada. Motivo: "+ex.message) : "Error al leer el archivo. Verifica que sea un Excel valido con las columnas esperadas (Cuenta, Nombre, Subcuenta de, Nombre de subcuenta de, Tipo, Activa, Moneda, Cuenta de mayor).");
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function handleImportMovFile(file) {
    setImportingMov(true); setImportErrorMov(null); setImportSummaryMov(null);
    var reader = new FileReader();
    reader.onload = function(ev) {
      try {
        var wb = XLSX.read(ev.target.result, {type:"array"});
        var ws = wb.Sheets[wb.SheetNames[0]];
        var rows = XLSX.utils.sheet_to_json(ws, {header:1, defval:"", raw:false});
        var parsed = parseMovimientosRows(rows);
        var errMsg = validarMovimientosParseados(parsed, cuentas);
        if (errMsg) { setImportErrorMov(errMsg); return; }
        setPendingImport({
          movimientos: parsed.movimientos,
          nombresPorCodigo: parsed.nombresPorCodigo,
          totalesArchivo: parsed.totalArchivo,
          rango: calcularRango(parsed.movimientos),
        });
      } catch (ex) {
        console.error("[EF-MOV-IMPORT] Fallo el parseo del archivo:", ex);
        setImportErrorMov(ex&&ex.message ? ("Error al leer el archivo. Motivo: "+ex.message) : "Error al leer el archivo. Verifica que sea un Excel valido con la estructura esperada (6 filas de encabezado, filas de cuenta con codigo XXX-XXX-XXX, filas de movimiento con fecha DD/MM/AAAA).");
      } finally {
        setImportingMov(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function handleConfirmImportMovimientos() {
    if (!pendingImport || savingMov) return;
    setSavingMov(true);
    try {
      var r = await apiFetch("/catalogs/"+uid+"/movimientos_contables/import", {
        method: "POST",
        body: JSON.stringify({
          movimientos: pendingImport.movimientos,
          nombresPorCodigo: pendingImport.nombresPorCodigo,
          totalesArchivo: pendingImport.totalesArchivo,
        }),
      });
      if (!r.ok) {
        var d = await r.json().catch(function(){return {};});
        throw new Error(d.error || ("El servidor rechazo la importacion (HTTP "+r.status+")."));
      }
      var body = await r.json();
      var fresh = await apiFetch("/catalogs/"+uid+"/movimientos_contables").then(function(x){return x.json();});
      setMovimientos(Array.isArray(fresh.data)?fresh.data:[]);
      setImportSummaryMov(body);
      setPendingImport(null);
    } catch (err) {
      console.error("[EF-MOV-IMPORT] Fallo la importacion:", err);
      setImportErrorMov("No se pudo completar la importacion. No se guardo nada. Motivo: "+(err.message||"error desconocido"));
      setPendingImport(null);
    } finally {
      setSavingMov(false);
    }
  }

  function exportarMovimientosExcel() {
    var data = filteredMov.map(function(r){
      return {
        "Nivel 1": r.nivel1, "Nivel 2": r.nivel2, "Nivel 3": r.nivel3, "Cuenta": r.cuenta, "Nombre": r.nombre,
        "Fecha": r.fecha, "Año": r.anio, "Mes": r.mes, "Tipo": r.tipo, "Número": r.numero, "Concepto": r.concepto,
        "Referencia": r.referencia, "Cargos": r.cargos, "Abonos": r.abonos, "Saldo": r.saldo,
        "Naturaleza": r.naturaleza, "Rubro": r.rubro,
      };
    });
    var ws = XLSX.utils.json_to_sheet(data, {header: COLUMNAS_MOV});
    ws['!autofilter'] = { ref: 'A1:' + XLSX.utils.encode_col(COLUMNAS_MOV.length-1) + (data.length+1) };
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
    XLSX.writeFile(wb, "movimientos_contables_"+uid+".xlsx");
  }

  var cuentasPorCodigo = useMemo(function(){
    var map = {};
    cuentas.forEach(function(c){ map[c.codigo]=c; });
    return map;
  },[cuentas]);

  var movimientosUnidos = useMemo(function(){
    return movimientos.map(function(m){
      var cta = cuentasPorCodigo[m.codigoCuenta] || null;
      var rn = splitRubroNaturaleza(cta?cta.tipo:"");
      var fecha = m.fecha||"";
      var signo = rn.naturaleza.toLowerCase()==="deudora" ? 1 : -1;
      var saldoFirmado = signo * (m.cargos - m.abonos);
      return {
        nivel1: m.codigoCuenta?m.codigoCuenta.slice(0,3):"",
        nivel2: m.codigoCuenta?m.codigoCuenta.slice(0,6):"",
        nivel3: m.codigoCuenta||"",
        cuenta: formatCuentaConGuiones(m.codigoCuenta),
        nombre: cta?cta.nombre:"—",
        fecha: fecha,
        anio: fecha?fecha.slice(0,4):"",
        mes: fecha?fecha.slice(5,7):"",
        tipo: m.tipo||"",
        numero: m.numero||"",
        concepto: m.concepto||"",
        referencia: m.referencia||"",
        cargos: m.cargos||0,
        abonos: m.abonos||0,
        saldo: saldoFirmado,
        naturaleza: rn.naturaleza,
        rubro: rn.rubro,
      };
    });
  },[movimientos, cuentasPorCodigo]);

  var filteredMov = useMemo(function(){
    var qNivel1 = filtroNivel1.trim().toLowerCase();
    var qNivel2 = filtroNivel2.trim().toLowerCase();
    var qNivel3 = filtroNivel3.trim().toLowerCase();
    var qCuenta = filtroCuenta.trim().toLowerCase();
    var qNombre = filtroNombre.trim().toLowerCase();
    var qAnio = filtroAnio.trim().toLowerCase();
    var qMes = filtroMes.trim().toLowerCase();
    var qTipo = filtroTipo.trim().toLowerCase();
    var qNumero = filtroNumero.trim().toLowerCase();
    var qConcepto = filtroConcepto.trim().toLowerCase();
    var qReferencia = filtroReferencia.trim().toLowerCase();
    var qNaturaleza = filtroNaturaleza.trim().toLowerCase();
    var qRubro = filtroRubro.trim().toLowerCase();
    var qGeneral = filtroGeneral.trim().toLowerCase();
    var cargosMin = filtroCargosMin!==""?parseFloat(filtroCargosMin):null;
    var cargosMax = filtroCargosMax!==""?parseFloat(filtroCargosMax):null;
    var abonosMin = filtroAbonosMin!==""?parseFloat(filtroAbonosMin):null;
    var abonosMax = filtroAbonosMax!==""?parseFloat(filtroAbonosMax):null;
    var saldoMin = filtroSaldoMin!==""?parseFloat(filtroSaldoMin):null;
    var saldoMax = filtroSaldoMax!==""?parseFloat(filtroSaldoMax):null;

    var list = movimientosUnidos.filter(function(r){
      if (qNivel1 && (r.nivel1||"").toLowerCase().indexOf(qNivel1)===-1) return false;
      if (qNivel2 && (r.nivel2||"").toLowerCase().indexOf(qNivel2)===-1) return false;
      if (qNivel3 && (r.nivel3||"").toLowerCase().indexOf(qNivel3)===-1) return false;
      if (qCuenta && (r.cuenta||"").toLowerCase().indexOf(qCuenta)===-1) return false;
      if (qTipo && (r.tipo||"").toLowerCase().indexOf(qTipo)===-1) return false;
      if (qNaturaleza && (r.naturaleza||"").toLowerCase().indexOf(qNaturaleza)===-1) return false;
      if (qRubro && (r.rubro||"").toLowerCase().indexOf(qRubro)===-1) return false;
      if (qAnio && (r.anio||"").toLowerCase().indexOf(qAnio)===-1) return false;
      if (qMes && (r.mes||"").toLowerCase().indexOf(qMes)===-1) return false;
      if (filtroDesde && r.fecha < filtroDesde) return false;
      if (filtroHasta && r.fecha > filtroHasta) return false;
      if (qNombre && (r.nombre||"").toLowerCase().indexOf(qNombre)===-1) return false;
      if (qConcepto && (r.concepto||"").toLowerCase().indexOf(qConcepto)===-1) return false;
      if (qReferencia && (r.referencia||"").toLowerCase().indexOf(qReferencia)===-1) return false;
      if (qNumero && (r.numero||"").toLowerCase().indexOf(qNumero)===-1) return false;
      if (cargosMin!==null && !isNaN(cargosMin) && r.cargos<cargosMin) return false;
      if (cargosMax!==null && !isNaN(cargosMax) && r.cargos>cargosMax) return false;
      if (abonosMin!==null && !isNaN(abonosMin) && r.abonos<abonosMin) return false;
      if (abonosMax!==null && !isNaN(abonosMax) && r.abonos>abonosMax) return false;
      if (saldoMin!==null && !isNaN(saldoMin) && r.saldo<saldoMin) return false;
      if (saldoMax!==null && !isNaN(saldoMax) && r.saldo>saldoMax) return false;
      if (qGeneral) {
        var matches = (r.cuenta||"").toLowerCase().indexOf(qGeneral)!==-1
          || (r.nombre||"").toLowerCase().indexOf(qGeneral)!==-1
          || (r.concepto||"").toLowerCase().indexOf(qGeneral)!==-1
          || (r.referencia||"").toLowerCase().indexOf(qGeneral)!==-1
          || (r.tipo||"").toLowerCase().indexOf(qGeneral)!==-1
          || (r.numero||"").toLowerCase().indexOf(qGeneral)!==-1;
        if (!matches) return false;
      }
      return true;
    });
    return list.slice().sort(function(a,b){
      if (a.fecha!==b.fecha) return a.fecha<b.fecha?-1:1;
      return (a.cuenta||"").localeCompare(b.cuenta||"");
    });
  },[
    movimientosUnidos,
    filtroNivel1, filtroNivel2, filtroNivel3, filtroCuenta,
    filtroTipo, filtroNaturaleza, filtroRubro, filtroAnio, filtroMes,
    filtroDesde, filtroHasta, filtroGeneral,
    filtroNombre, filtroConcepto, filtroReferencia, filtroNumero,
    filtroCargosMin, filtroCargosMax, filtroAbonosMin, filtroAbonosMax, filtroSaldoMin, filtroSaldoMax,
  ]);

  var totalPagesMov = Math.max(1, Math.ceil(filteredMov.length/PAGE_SIZE));
  var pageMovClamped = Math.min(pageMov, totalPagesMov-1);
  var pageRowsMov = filteredMov.slice(pageMovClamped*PAGE_SIZE, pageMovClamped*PAGE_SIZE+PAGE_SIZE);

  var filtered = cuentas.filter(function(c){
    if (!search.trim()) return true;
    var q = search.trim().toLowerCase();
    return (c.codigo||"").toLowerCase().indexOf(q)!==-1 || (c.nombre||"").toLowerCase().indexOf(q)!==-1;
  }).slice().sort(function(a,b){ return (a.codigo||"").localeCompare(b.codigo||""); });

  var totalPages = Math.max(1, Math.ceil(filtered.length/PAGE_SIZE));
  var pageClamped = Math.min(page, totalPages-1);
  var pageRows = filtered.slice(pageClamped*PAGE_SIZE, pageClamped*PAGE_SIZE+PAGE_SIZE);

  var rows = pageRows.map(function(c){
    return (
      <tr key={c.codigo} style={{borderBottom:"1px solid #f0f0f0"}}>
        <td style={{padding:"8px 12px",color:"#374151",fontWeight:600,whiteSpace:"nowrap"}}>{c.codigo}</td>
        <td style={{padding:"8px 12px",color:"#374151"}}>{c.nombre}</td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:12}}>{c.subcuentaDe?(c.subcuentaDe+(c.nombreSubcuentaDe?" — "+c.nombreSubcuentaDe:"")):"—"}</td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:12,whiteSpace:"nowrap"}}>{c.tipo}</td>
        <td style={{padding:"8px 12px"}}><span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:c.activa?"#dcfce7":"#f1f5f9",color:c.activa?"#16a34a":"#94a3b8",fontWeight:700}}>{c.activa?"Activa":"Inactiva"}</span></td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:12}}>{c.moneda}</td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:12,whiteSpace:"nowrap"}}>{c.cuentaDeMayor}</td>
        <td style={{padding:"8px 12px"}}><span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:BRAND+"15",color:BRAND,fontWeight:700}}>{c.origen}</span></td>
        <td style={{padding:"8px 12px",whiteSpace:"nowrap"}}>
          <button onClick={function(){setFormState(c);setFormError("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"#64748b",fontWeight:600,marginRight:10}} onMouseEnter={function(e){e.currentTarget.style.color=BRAND;}} onMouseLeave={function(e){e.currentTarget.style.color="#64748b";}}>Editar</button>
          <button onClick={function(){setConfirmDelete(c);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"#94a3b8",fontWeight:600}} onMouseEnter={function(e){e.currentTarget.style.color="#ef4444";}} onMouseLeave={function(e){e.currentTarget.style.color="#94a3b8";}}>Eliminar</button>
        </td>
      </tr>
    );
  });

  var rowsMov = pageRowsMov.map(function(r,i){
    return (
      <tr key={r.cuenta+"|"+r.fecha+"|"+r.numero+"|"+i} style={{borderBottom:"1px solid #f0f0f0"}}>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:11,whiteSpace:"nowrap"}}>{r.nivel1}</td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:11,whiteSpace:"nowrap"}}>{r.nivel2}</td>
        <td style={{padding:"8px 12px",color:"#64748b",fontSize:11,whiteSpace:"nowrap"}}>{r.nivel3}</td>
        <td style={{padding:"8px 12px",color:"#374151",fontWeight:600,whiteSpace:"nowrap"}}>{r.cuenta}</td>
        <td style={{padding:"8px 12px",color:"#374151",whiteSpace:"nowrap"}}>{r.nombre}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.fecha}</td>
        <td style={{padding:"8px 12px",color:"#64748b"}}>{r.anio}</td>
        <td style={{padding:"8px 12px",color:"#64748b"}}>{r.mes}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.tipo}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.numero}</td>
        <td style={{padding:"8px 12px",color:"#374151"}}>{r.concepto}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.referencia}</td>
        <td style={{padding:"8px 12px",color:"#374151",textAlign:"right",whiteSpace:"nowrap"}}>{r.cargos.toFixed(2)}</td>
        <td style={{padding:"8px 12px",color:"#374151",textAlign:"right",whiteSpace:"nowrap"}}>{r.abonos.toFixed(2)}</td>
        <td style={{padding:"8px 12px",color:"#374151",textAlign:"right",whiteSpace:"nowrap"}}>{r.saldo.toFixed(2)}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.naturaleza}</td>
        <td style={{padding:"8px 12px",color:"#64748b",whiteSpace:"nowrap"}}>{r.rubro}</td>
      </tr>
    );
  });

  var navBtns = EF_TABS.map(function(t){
    return (
      <button key={t.k} onClick={function(){setActiveTab(t.k);}} style={{display:"block",width:"100%",textAlign:"left",padding:"9px 12px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:activeTab===t.k?"rgba(255,255,255,0.2)":"transparent",color:"#fff",opacity:activeTab===t.k?1:0.65}}>
        {t.l}
      </button>
    );
  });

  return (
    <div style={{display:"flex",minHeight:"100vh",fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <ConfirmModal
        data={confirmDelete ? {msg:"¿Eliminar la cuenta "+confirmDelete.codigo+" — "+confirmDelete.nombre+"? Esta accion no se puede deshacer."} : null}
        onCancel={function(){setConfirmDelete(null);}}
        onConfirm={handleConfirmDelete}
      />
      <ImportMovConfirmModal
        data={pendingImport}
        saving={savingMov}
        onCancel={function(){setPendingImport(null);}}
        onConfirm={handleConfirmImportMovimientos}
      />
      {formState?(
        <CuentaFormModal
          cuentas={cuentas}
          editing={formState.codigo?formState:null}
          saving={saving}
          error={formError}
          onSave={handleSaveAccount}
          onClose={function(){setFormState(null);setFormError("");}}
        />
      ):null}

      <div style={{width:210,background:BRAND,minHeight:"100vh",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"20px 16px 16px",borderBottom:"1px solid rgba(255,255,255,0.1)",display:"flex",alignItems:"center",gap:10}}>
          <KofidesLogo size={28}/>
          <div>
            <p style={{color:"#fff",fontWeight:800,fontSize:15,margin:0,fontFamily:"Georgia,serif",letterSpacing:2}}>KOFIDES</p>
            <p style={{color:"rgba(255,255,255,0.5)",fontSize:11,margin:"2px 0 0"}}>Estados Financieros</p>
          </div>
        </div>
        <div style={{flex:1,padding:"12px 8px",display:"flex",flexDirection:"column",gap:2}}>
          {navBtns}
          <div style={{flex:1}}/>
          <div style={{borderTop:"1px solid rgba(255,255,255,0.1)",paddingTop:10,marginTop:6}}>
            <button onClick={p.onBack} style={{display:"block",width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:"transparent",color:"rgba(255,255,255,0.55)"}}>Inicio</button>
            <button onClick={p.onLogout} style={{display:"block",width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:"transparent",color:"rgba(255,100,100,0.8)"}}>Cerrar sesion</button>
          </div>
        </div>
      </div>

      <div style={{flex:1,background:"#f1f5f9",minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      <div style={{padding:24,flex:1}}>
        <h1 style={{fontSize:20,fontWeight:800,color:"#0f172a",margin:"0 0 6px"}}>Estados Financieros</h1>
        <p style={{color:"#64748b",fontSize:13,margin:"0 0 16px"}}>Fase 1: catalogo contable del cliente. Fase 2: movimientos auxiliares del catalogo, importados por rango de fechas. Cedulas: papeles de trabajo del contador.</p>

        {showClientSelector?(
          <div style={{marginBottom:16,maxWidth:320}}>
            <Lbl c="Cliente"/>
            <Slct value={p.user.id} onChange={function(e){
              var newId=parseInt(e.target.value,10);
              var c=p.clients.find(function(x){return x.id===newId;});
              if(c) p.onChangeClient(c);
            }}>
              {p.clients.map(function(c){return <option key={c.id} value={c.id}>{c.name}</option>;})}
            </Slct>
          </div>
        ):null}

        {activeTab==="catalogo"?(
        <>
        {actionError?(
          <div style={{marginBottom:16,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <p style={{margin:0,fontSize:13,fontWeight:700,color:"#dc2626"}}>⚠️ {actionError}</p>
              <button onClick={function(){setActionError(null);}} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:16,lineHeight:1,flexShrink:0}}>X</button>
            </div>
          </div>
        ):null}

        {importError?(
          <div style={{marginBottom:16,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <p style={{margin:0,fontSize:13,fontWeight:700,color:"#dc2626"}}>⚠️ No se importo el catalogo</p>
              <button onClick={function(){setImportError(null);}} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:16,lineHeight:1}}>X</button>
            </div>
            <p style={{margin:"6px 0 0",fontSize:12,color:"#991b1b"}}>{importError}</p>
          </div>
        ):null}

        {importSummary?(
          <div style={{marginBottom:16,background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <p style={{margin:0,fontSize:13,fontWeight:700,color:"#0f172a"}}>Importacion completada</p>
              <button onClick={function(){setImportSummary(null);}} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:16,lineHeight:1}}>X</button>
            </div>
            <div style={{display:"flex",gap:18,flexWrap:"wrap",marginTop:8,fontSize:12,color:"#374151"}}>
              <span>✅ {importSummary.added} cuenta(s) nueva(s)</span>
              <span>♻️ {importSummary.updated} cuenta(s) actualizada(s)</span>
              <span>📄 {importSummary.total} cuenta(s) en total</span>
            </div>
          </div>
        ):null}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:16}}>
          <Inp placeholder="Buscar por codigo o nombre..." value={search} onChange={function(e){setSearch(e.target.value);}} sx={{maxWidth:320}}/>
          <div style={{display:"flex",gap:10}}>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} disabled={importing} onChange={function(e){
              var f=e.target.files[0];
              e.target.value="";
              if(f) handleImportFile(f);
            }}/>
            <Btn onClick={function(){fileInputRef.current.click();}} disabled={importing} bg="#fff" sx={{color:BRAND,border:"1px solid "+BRAND,opacity:importing?0.7:1}}>{importing?"Importando...":"Importar Excel"}</Btn>
            <Btn onClick={function(){setFormState({});setFormError("");}}>+ Agregar cuenta manualmente</Btn>
          </div>
        </div>

        <Card sx={{padding:0}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Codigo</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Nombre</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Subcuenta de</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Tipo</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Estado</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Moneda</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Cuenta de mayor</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Origen</th>
                  <th style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11}}>Acciones</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
          {!loaded?(
            <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>Cargando catalogo...</div>
          ):(filtered.length===0?(
            <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>{cuentas.length===0?"Este cliente aun no tiene cuentas. Importa un Excel o agrega la primera cuenta manualmente.":"No hay cuentas que coincidan con la busqueda."}</div>
          ):null)}
        </Card>

        {filtered.length>0?(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,fontSize:12,color:"#64748b"}}>
            <span>{filtered.length+" cuenta(s)"+(search.trim()?" encontradas":" en total")}</span>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={function(){setPage(function(pg){return Math.max(0,pg-1);});}} disabled={pageClamped===0} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #dde3f0",background:"#fff",fontSize:12,cursor:pageClamped===0?"default":"pointer",color:pageClamped===0?"#cbd5e1":"#374151"}}>Anterior</button>
              <span>{"Pagina "+(pageClamped+1)+" de "+totalPages}</span>
              <button onClick={function(){setPage(function(pg){return Math.min(totalPages-1,pg+1);});}} disabled={pageClamped>=totalPages-1} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #dde3f0",background:"#fff",fontSize:12,cursor:pageClamped>=totalPages-1?"default":"pointer",color:pageClamped>=totalPages-1?"#cbd5e1":"#374151"}}>Siguiente</button>
            </div>
          </div>
        ):null}
        </>
        ):activeTab==="movimientos"?(
        <>
        {importErrorMov?(
          <div style={{marginBottom:16,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <p style={{margin:0,fontSize:13,fontWeight:700,color:"#dc2626"}}>⚠️ No se importaron los movimientos</p>
              <button onClick={function(){setImportErrorMov(null);}} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:16,lineHeight:1}}>X</button>
            </div>
            <p style={{margin:"6px 0 0",fontSize:12,color:"#991b1b"}}>{importErrorMov}</p>
          </div>
        ):null}

        {importSummaryMov?(
          <div style={{marginBottom:16,background:"#fff",border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <p style={{margin:0,fontSize:13,fontWeight:700,color:"#0f172a"}}>Importacion completada</p>
              <button onClick={function(){setImportSummaryMov(null);}} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:16,lineHeight:1}}>X</button>
            </div>
            <div style={{display:"flex",gap:18,flexWrap:"wrap",marginTop:8,fontSize:12,color:"#374151"}}>
              <span>📅 Rango: {importSummaryMov.rango.desde} a {importSummaryMov.rango.hasta}</span>
              <span>🗑️ {importSummaryMov.eliminados} eliminado(s) del rango</span>
              <span>✅ {importSummaryMov.insertados} insertado(s)</span>
              <span>📄 {importSummaryMov.total} movimiento(s) en total</span>
            </div>
          </div>
        ):null}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:14,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"flex-end",gap:14,flexWrap:"wrap"}}>
            <div><Lbl c="Desde"/><Inp type="date" value={filtroDesde} onChange={function(e){setFiltroDesde(e.target.value);}} sx={{maxWidth:170}}/></div>
            <div><Lbl c="Hasta"/><Inp type="date" value={filtroHasta} onChange={function(e){setFiltroHasta(e.target.value);}} sx={{maxWidth:170}}/></div>
            <div><Lbl c="Buscar en todas las columnas"/><Inp placeholder="Buscar por cuenta, nombre, concepto, referencia..." value={filtroGeneral} onChange={function(e){setFiltroGeneral(e.target.value);}} sx={{minWidth:280}}/></div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <input ref={fileInputMovRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} disabled={importingMov} onChange={function(e){
              var f=e.target.files[0];
              e.target.value="";
              if(f) handleImportMovFile(f);
            }}/>
            <Btn onClick={function(){fileInputMovRef.current.click();}} disabled={importingMov} bg="#fff" sx={{color:BRAND,border:"1px solid "+BRAND,opacity:importingMov?0.7:1}}>{importingMov?"Leyendo...":"Importar Excel"}</Btn>
            <Btn onClick={exportarMovimientosExcel} disabled={filteredMov.length===0} bg="#fff" sx={{color:BRAND,border:"1px solid "+BRAND,opacity:filteredMov.length===0?0.5:1}}>Exportar Excel</Btn>
          </div>
        </div>

        <Card sx={{padding:0}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
                  {COLUMNAS_MOV.map(function(col){
                    return <th key={col} style={{padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11,whiteSpace:"nowrap"}}>{col}</th>;
                  })}
                </tr>
                <tr style={{borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNivel1} onChange={function(e){setFiltroNivel1(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNivel2} onChange={function(e){setFiltroNivel2(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNivel3} onChange={function(e){setFiltroNivel3(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroCuenta} onChange={function(e){setFiltroCuenta(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNombre} onChange={function(e){setFiltroNombre(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}></th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroAnio} onChange={function(e){setFiltroAnio(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroMes} onChange={function(e){setFiltroMes(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroTipo} onChange={function(e){setFiltroTipo(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNumero} onChange={function(e){setFiltroNumero(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroConcepto} onChange={function(e){setFiltroConcepto(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroReferencia} onChange={function(e){setFiltroReferencia(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <Inp type="number" placeholder="Min" value={filtroCargosMin} onChange={function(e){setFiltroCargosMin(e.target.value);}} sx={filterCtrlSx}/>
                      <Inp type="number" placeholder="Max" value={filtroCargosMax} onChange={function(e){setFiltroCargosMax(e.target.value);}} sx={filterCtrlSx}/>
                    </div>
                  </th>
                  <th style={thFilterStyle}>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <Inp type="number" placeholder="Min" value={filtroAbonosMin} onChange={function(e){setFiltroAbonosMin(e.target.value);}} sx={filterCtrlSx}/>
                      <Inp type="number" placeholder="Max" value={filtroAbonosMax} onChange={function(e){setFiltroAbonosMax(e.target.value);}} sx={filterCtrlSx}/>
                    </div>
                  </th>
                  <th style={thFilterStyle}>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <Inp type="number" placeholder="Min" value={filtroSaldoMin} onChange={function(e){setFiltroSaldoMin(e.target.value);}} sx={filterCtrlSx}/>
                      <Inp type="number" placeholder="Max" value={filtroSaldoMax} onChange={function(e){setFiltroSaldoMax(e.target.value);}} sx={filterCtrlSx}/>
                    </div>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroNaturaleza} onChange={function(e){setFiltroNaturaleza(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                  <th style={thFilterStyle}>
                    <Inp placeholder="Contiene..." value={filtroRubro} onChange={function(e){setFiltroRubro(e.target.value);}} sx={filterCtrlSx}/>
                  </th>
                </tr>
              </thead>
              <tbody>{rowsMov}</tbody>
            </table>
          </div>
          {!loadedMov?(
            <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>Cargando movimientos...</div>
          ):(filteredMov.length===0?(
            <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>{movimientos.length===0?"Este cliente aun no tiene movimientos. Importa un Excel de Movimientos Auxiliares del Catalogo.":"No hay movimientos que coincidan con los filtros."}</div>
          ):null)}
        </Card>

        {filteredMov.length>0?(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,fontSize:12,color:"#64748b"}}>
            <span>{filteredMov.length+" movimiento(s)"+(filteredMov.length!==movimientosUnidos.length?" encontrados":" en total")}</span>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={function(){setPageMov(function(pg){return Math.max(0,pg-1);});}} disabled={pageMovClamped===0} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #dde3f0",background:"#fff",fontSize:12,cursor:pageMovClamped===0?"default":"pointer",color:pageMovClamped===0?"#cbd5e1":"#374151"}}>Anterior</button>
              <span>{"Pagina "+(pageMovClamped+1)+" de "+totalPagesMov}</span>
              <button onClick={function(){setPageMov(function(pg){return Math.min(totalPagesMov-1,pg+1);});}} disabled={pageMovClamped>=totalPagesMov-1} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #dde3f0",background:"#fff",fontSize:12,cursor:pageMovClamped>=totalPagesMov-1?"default":"pointer",color:pageMovClamped>=totalPagesMov-1?"#cbd5e1":"#374151"}}>Siguiente</button>
            </div>
          </div>
        ):null}
        </>
        ):(
        <CedulasModule uid={uid} cuentas={cuentas} movimientos={movimientos} clientName={p.user.name} onUpdateRfcManual={handleUpdateRfcManual}/>
        )}
      </div>
      </div>
    </div>
  );
}
